/**
 * XPEN-2529 / XPEN-2812: per-line missing-required-field detection for invoice
 * editors. Pure functions only — components own lookup + caching.
 */

import Utils from './utils';
import {
    buildActivityAdditionalFieldsFilter,
    buildAderantRequiredFieldsFilter,
    buildBillableAdditionalFieldsFilter,
    buildGlAdditionalFieldsFilter,
    effectiveLineAttorney,
} from './additional-fields-filter-helpers';

const SEGMENT_KEYS = ['gl_unit', 'gl_office', 'gl_section', 'gl_departement', 'gl_timekeeper', 'gl_practice_group'];
const SEGMENT_CAMEL = {
    gl_unit: 'GLUnit',
    gl_office: 'GLOffice',
    gl_section: 'GLSection',
    gl_departement: 'GLDepartement',
    gl_timekeeper: 'GLTimekeeper',
    gl_practice_group: 'GLPracticeGroup',
};
const SYNCED_SEGMENT_KEYS = new Set(['gl_section', 'gl_practice_group']);

const ADERANT_MODAL_KEYS = new Set([
    'invoice.required_field_employee',
    'invoice.required_field_department',
    'invoice.required_field_incurring_employee',
    'invoice.required_field_authorizing_employee',
]);

/** Missing keys for fields edited inline in the invoice line grid — no modal fix. */
export const INLINE_GRID_REQUIRED_FIELD_KEYS = new Set([
    'invoice.required_field_matter',
    'invoice.required_field_cost_type',
    'invoice.required_field_gl_natural',
    'invoice.required_field_description',
]);

function lineValueMissing(stored) {
    if (stored == null) return true;
    if (typeof stored === 'string') return stored.trim() === '';
    if (typeof stored === 'object') {
        const v = stored.value;
        return v == null || (typeof v === 'string' && v.trim() === '');
    }
    return false;
}

function segmentValueFromLine(key, item, invoiceJson, segCfg) {
    const camel = SEGMENT_CAMEL[key];
    if (segCfg[camel]?.active !== true) return undefined;
    const defaultVal = segCfg[camel]?.default_value;
    const lineVal = item?.[camel];
    if (SYNCED_SEGMENT_KEYS.has(key)) {
        // A synced segment can be a picked {code, name} object or a bare code
        // string the engine/api inferred (GLNatural-derived section, header
        // cascade); accept both so an inferred value is not seen as missing.
        const code = (lineVal && typeof lineVal === 'object') ? lineVal.code : lineVal;
        if (code != null && String(code).trim() !== '') return String(code);
        if (defaultVal != null && String(defaultVal).trim() !== '') return String(defaultVal);
        return undefined;
    }
    if (key === 'gl_unit') {
        const v = lineVal ?? invoiceJson?.GLUnit ?? defaultVal;
        if (v != null && String(v).trim() !== '') return String(v);
        return undefined;
    }
    if (lineVal != null && String(lineVal).trim() !== '') return String(lineVal);
    if (defaultVal != null && String(defaultVal).trim() !== '') return String(defaultVal);
    return undefined;
}

export function supportsProactiveRequiredFieldHints(data) {
    return Utils.isExportFormat(data, 'aderant')
        || Utils.isExportFormat(data, 'elite')
        || Utils.isExportFormat(data, 'juris_format')
        || Utils.isExportFormat(data, 'juris_lexis_nexis')
        || Utils.isExportFormat(data, 'surepoint');
}

// XPEN-3445 / XPEN-3533: a billable line posts to the client cost account, which
// SurePoint accepts with a client and matter, or with a cost attorney when the line
// has no matter; firm lines take the configured firm account.
// XPEN-3543: the attorney may come from the invoice header, so a line with no
// attorney of its own is still covered when the header carries one.
export function missingRequiredFieldsForSurePointLine(item, isBillable, invoiceJson) {
    if (isBillable
        && lineValueMissing(item?.MatterId)
        && lineValueMissing(effectiveLineAttorney(item, invoiceJson))) {
        return ['invoice.required_field_attorney'];
    }
    return [];
}

export function mergeMissingFieldKeys(...arrays) {
    const seen = new Set();
    const merged = [];
    arrays.forEach((keys) => {
        (keys || []).forEach((key) => {
            if (!seen.has(key)) {
                seen.add(key);
                merged.push(key);
            }
        });
    });
    return merged;
}

/**
 * Shared Elite Activity rule — matter `pta_group_cost` must match line Activity.
 */
export function getActivityMissingKeys(item, matter) {
    if (!matter?.pta_group_cost) return [];
    if (!item?.Activity) return ['invoice.required_field_activity'];
    if (matter.pta_group_cost !== item.Activity.activity_list) {
        return ['invoice.required_field_activity'];
    }
    return [];
}

/**
 * Return the i18n keys for the required fields that are missing on a GL line.
 *
 * ## Two flag shapes the API surfaces, one meaning
 *
 * The XPEN-2528 search-route response can carry the flags two ways:
 *   - explicit `null` (route-level dict comp on `/gl/nat/search/` and
 *     `/cost_type/search/` — these set every flag key to `doc.get(...)`),
 *   - absent key entirely (`/gl_naturals/` no-pagination Mongo projection,
 *     which simply doesn't list the field on docs that don't carry it).
 * Both shapes mean "no gating applies" and resolve to the same code path
 * here — the `glAccount.is_employee_required && ...` truthy check below
 * collapses `null` and `undefined` to the same outcome. Don't switch to
 * `'is_employee_required' in glAccount` membership testing — that breaks
 * the explicit-null path silently.
 */
export function missingRequiredFieldsForGlLine(item, glAccount) {
    if (!item || !glAccount) return [];
    const missing = [];
    if (glAccount.is_employee_required && lineValueMissing(item.EmployeeId)) {
        missing.push('invoice.required_field_employee');
    }
    if (glAccount.is_department_required && lineValueMissing(item.DepartmentId)) {
        missing.push('invoice.required_field_department');
    }
    // `is_profit_center_required` is deliberately NOT surfaced. Aderant
    // tenants carry exactly one profit center; the modal hides the
    // ProfitCenter dropdown and the export adapter's `_build_gl_line`
    // falls back through `_infer_dimension(line_value=None,
    // default_value=book_defaults.overhead_profit_center_id)` to the
    // single configured value. Showing a warning for a field the user
    // can neither see nor fix would be a false positive. The "never
    // renders ProfitCenter on Aderant" tombstone in the modal tests is
    // the canary if anyone tries to re-add it.
    return missing;
}

export function missingRequiredFieldsForDisbursementLine(item, costType) {
    if (!item || !costType) return [];
    const missing = [];
    if (costType.require_incurring_employee) {
        const incurring = item.IncurringEmployeeId ?? item.EmployeeId;
        if (lineValueMissing(incurring)) {
            missing.push('invoice.required_field_incurring_employee');
        }
    }
    if (costType.require_authorizing_employee && lineValueMissing(item.AuthorizingEmployeeId)) {
        missing.push('invoice.required_field_authorizing_employee');
    }
    return missing;
}

export function missingRequiredFieldsForEliteLineSync(item, isBillable) {
    if (isBillable) return [];
    if (lineValueMissing(item?.GLNatural)) {
        return ['invoice.required_field_gl_natural'];
    }
    return [];
}

export function missingRequiredFieldsForEliteLineWithLookups({ item, isBillable, matter, glSuggestResult }) {
    if (isBillable) {
        return getActivityMissingKeys(item, matter);
    }
    if (!glSuggestResult || lineValueMissing(item?.GLNatural)) return [];
    const total = Number(glSuggestResult?.meta?.totalRowCount);
    const matchMode = glSuggestResult?.meta?.match_mode || 'strict';
    if (matchMode === 'strict' && total === 0) {
        return ['invoice.required_field_gl_combination'];
    }
    return [];
}

export function missingRequiredFieldsForJurisLineSync(item, isBillable, data) {
    const missing = [];
    if (isBillable) {
        if (lineValueMissing(item?.MatterId)) missing.push('invoice.required_field_matter');
        if (lineValueMissing(item?.CostType)) missing.push('invoice.required_field_cost_type');
    } else {
        if (lineValueMissing(item?.GLNatural)) missing.push('invoice.required_field_gl_natural');
        if (Utils.isEngineType(data, 'legal') && lineValueMissing(item?.Office)) {
            missing.push('invoice.required_field_office');
        }
    }
    return missing;
}

export function missingRequiredFieldsForJurisLineWithLookups({
    item, isBillable, matter, accountOrType, officeLookup, data,
}) {
    const missing = [];
    if (isBillable) {
        if (!lineValueMissing(item?.MatterId) && !matter) {
            missing.push('invoice.required_field_matter');
        }
        if (!lineValueMissing(item?.CostType) && !accountOrType) {
            missing.push('invoice.required_field_cost_type');
        }
    } else {
        if (!lineValueMissing(item?.GLNatural) && !accountOrType) {
            missing.push('invoice.required_field_gl_natural');
        }
        if (
            Utils.isEngineType(data, 'legal')
            && !lineValueMissing(item?.Office)
            && !officeLookup
        ) {
            missing.push('invoice.required_field_office');
        }
    }
    return missing;
}

// XPEN-3620: LexisNexis Juris is reached by file import, not by an API, so the
// rules here are the ones its expense import refuses a row on — the matter the
// cost posts to, the expense code and the narrative. No office: the office is
// carried by the GL account itself and, on the check file, by the report
// owner's home office, never by the line.
export function missingRequiredFieldsForJurisLexisNexisLineSync(item, isBillable) {
    const missing = [];
    if (isBillable) {
        if (lineValueMissing(item?.MatterId)) missing.push('invoice.required_field_matter');
        if (lineValueMissing(item?.CostType)) missing.push('invoice.required_field_cost_type');
        if (lineValueMissing(item?.Description)) missing.push('invoice.required_field_description');
    } else if (lineValueMissing(item?.GLNatural)) {
        missing.push('invoice.required_field_gl_natural');
    }
    return missing;
}

export function missingRequiredFieldsForJurisLexisNexisLineWithLookups({
    item, isBillable, matter, accountOrType,
}) {
    const missing = [];
    if (isBillable) {
        if (!lineValueMissing(item?.MatterId) && !matter) {
            missing.push('invoice.required_field_matter');
        }
        if (!lineValueMissing(item?.CostType) && !accountOrType) {
            missing.push('invoice.required_field_cost_type');
        }
    } else if (!lineValueMissing(item?.GLNatural) && !accountOrType) {
        missing.push('invoice.required_field_gl_natural');
    }
    return missing;
}

export function buildEliteGlSuggestFiltersFromLine(item, invoiceJson, config) {
    const segCfg = config?.gl_account_segment_configuration ?? {};
    const filters = {};
    if (item?.GLNatural) filters.gl_natural = String(item.GLNatural);
    SEGMENT_KEYS.forEach((key) => {
        const val = segmentValueFromLine(key, item, invoiceJson, segCfg);
        if (val !== undefined) filters[key] = val;
    });
    return filters;
}

export function buildRequiredFieldsModalFilter(data, missingForLine, { isBillable = false } = {}) {
    if (!missingForLine?.length) return null;
    const modalKeys = missingForLine.filter((key) => !INLINE_GRID_REQUIRED_FIELD_KEYS.has(key));
    if (modalKeys.length === 0) return null;
    if (modalKeys.includes('invoice.required_field_activity')) {
        return buildActivityAdditionalFieldsFilter(data);
    }
    if (Utils.isExportFormat(data, 'aderant') && modalKeys.some((k) => ADERANT_MODAL_KEYS.has(k))) {
        return buildAderantRequiredFieldsFilter(data, { isBillable });
    }
    const context = { isLegalEngine: Utils.isEngineType(data, 'legal') };
    return isBillable
        ? buildBillableAdditionalFieldsFilter(data, context)
        : buildGlAdditionalFieldsFilter(data, context);
}

/**
 * Decide whether the warning icon opens a modal, the GL picker, or is tooltip-only.
 */
export function resolveRequiredFieldsIconAction(data, missingForLine, { isBillable = false } = {}) {
    if (!missingForLine?.length) return { type: 'none' };
    if (missingForLine.includes('invoice.required_field_gl_combination')) {
        return { type: 'gl-picker' };
    }
    const filter = buildRequiredFieldsModalFilter(data, missingForLine, { isBillable });
    if (filter?.length) {
        return { type: 'modal', filter };
    }
    return { type: 'tooltip-only' };
}

/**
 * ERP dispatcher for proactive missing-field hints on invoice lines.
 */
export function missingRequiredFieldsForLine({
    data,
    item,
    accountOrType,
    isBillable,
    matter,
    glSuggestResult,
    officeLookup,
    invoiceJson,
}) {
    if (!item) return [];
    if (!data || Utils.isExportFormat(data, 'aderant')) {
        if (isBillable) {
            return missingRequiredFieldsForDisbursementLine(item, accountOrType);
        }
        return missingRequiredFieldsForGlLine(item, accountOrType);
    }
    if (Utils.isExportFormat(data, 'elite')) {
        return mergeMissingFieldKeys(
            missingRequiredFieldsForEliteLineSync(item, isBillable),
            missingRequiredFieldsForEliteLineWithLookups({
                item, isBillable, matter, glSuggestResult,
            }),
        );
    }
    if (Utils.isExportFormat(data, 'surepoint')) {
        return missingRequiredFieldsForSurePointLine(item, isBillable, invoiceJson);
    }
    if (Utils.isExportFormat(data, 'juris_format')) {
        return mergeMissingFieldKeys(
            missingRequiredFieldsForJurisLineSync(item, isBillable, data),
            missingRequiredFieldsForJurisLineWithLookups({
                item, isBillable, matter, accountOrType, officeLookup, data,
            }),
        );
    }
    if (Utils.isExportFormat(data, 'juris_lexis_nexis')) {
        return mergeMissingFieldKeys(
            missingRequiredFieldsForJurisLexisNexisLineSync(item, isBillable),
            missingRequiredFieldsForJurisLexisNexisLineWithLookups({
                item, isBillable, matter, accountOrType,
            }),
        );
    }
    return [];
}
